import React from 'react';
import { ClassCode, CourseConfiguration, DRF_2025_QUOTAS } from '../types/drf-rules';

interface CourseSettingsProps {
  config: CourseConfiguration;
  onChange: (config: Partial<CourseConfiguration>) => void;
}

const CourseSettings: React.FC<CourseSettingsProps> = ({ config, onChange }) => {
  const classOptions: { code: ClassCode; name: string; category: string }[] = [
    { code: 'LF', name: 'LF (60-70cm)', category: 'Starter klasser' },
    { code: 'LE', name: 'LE (70-80cm)', category: 'Starter klasser' },
    { code: 'LD', name: 'LD (80-90cm)', category: 'Starter klasser' },
    { code: 'LC', name: 'LC (90-100cm)', category: 'Begynder' },
    { code: 'LB1', name: 'LB1 (100-110cm)', category: 'Let øvet' },
    { code: 'LB2', name: 'LB2 (100-110cm)', category: 'Let øvet' },
    { code: 'LA1', name: 'LA1 (110-120cm)', category: 'Let øvet' },
    { code: 'LA2', name: 'LA2 (110-120cm)', category: 'Let øvet' },
    { code: 'MB1', name: 'MB1 (120-130cm)', category: 'Mellem' },
    { code: 'MB2', name: 'MB2 (120-130cm)', category: 'Mellem' },
    { code: 'MA1', name: 'MA1 (130-140cm)', category: 'Mellem' },
    { code: 'MA2', name: 'MA2 (130-140cm)', category: 'Mellem' },
    { code: 'S1', name: 'S1 (135-145cm)', category: 'Svær' },
    { code: 'S2', name: 'S2 (140-150cm)', category: 'Svær' },
    { code: 'S3', name: 'S3 (145-155cm)', category: 'Svær' },
    { code: 'S4', name: 'S4 (150-160cm)', category: 'Svær' },
    { code: 'Freestyle', name: 'Freestyle (50-180cm)', category: 'Speciel' }
  ];

  const groupedClasses = classOptions.reduce((acc, option) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {} as Record<string, typeof classOptions>);

  const quota = DRF_2025_QUOTAS[config.classCode];

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Bane Indstillinger</h3>
      
      <div className="space-y-6">
        {/* Class Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Konkurrence Klasse
          </label>
          <select
            value={config.classCode}
            onChange={(e) => onChange({ classCode: e.target.value as ClassCode })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {Object.entries(groupedClasses).map(([category, options]) => (
              <optgroup key={category} label={category}>
                {options.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Arena Dimensions */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bredde (m)
            </label>
            <input
              type="number"
              min="10"
              max="150"
              value={config.arenaWidth}
              onChange={(e) => onChange({ arenaWidth: parseInt(e.target.value) || 60 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Længde (m)
            </label>
            <input
              type="number"
              min="10"
              max="150"
              value={config.arenaLength}
              onChange={(e) => onChange({ arenaLength: parseInt(e.target.value) || 80 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Indoor/Outdoor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Arena Type
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={!config.isIndoor}
                onChange={() => onChange({ isIndoor: false })}
                className="mr-2 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Udendørs</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={config.isIndoor}
                onChange={() => onChange({ isIndoor: true })}
                className="mr-2 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Indendørs</span>
            </label>
          </div>
        </div>

        {/* Horse/Pony Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Deltager Type
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={config.animalType === 'horse'}
                onChange={() => onChange({ animalType: 'horse' })}
                className="mr-2 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Hest</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={config.animalType === 'pony'}
                onChange={() => onChange({ animalType: 'pony' })}
                className="mr-2 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Pony</span>
            </label>
          </div>
        </div>

        {/* Class Information */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Klasse Krav</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Spring højde:</span>
              <span className="ml-2 font-medium">{quota.heightMin}-{quota.heightMax}cm</span>
            </div>
            <div>
              <span className="text-gray-600">Antal spring:</span>
              <span className="ml-2 font-medium">{quota.minObstacles}-{quota.maxObstacles}</span>
            </div>
            <div>
              <span className="text-gray-600">Max efforts:</span>
              <span className="ml-2 font-medium">{quota.maxEfforts}</span>
            </div>
            <div>
              <span className="text-gray-600">Kombinationer:</span>
              <span className="ml-2 font-medium">{quota.minCombos}-{quota.maxCombos}</span>
            </div>
            {quota.waterMaxCm && (
              <div className="col-span-2">
                <span className="text-gray-600">Vandgrav bredde:</span>
                <span className="ml-2 font-medium">
                  {quota.waterMinCm ? `${quota.waterMinCm}-` : 'max '}
                  {quota.waterMaxCm}cm
                </span>
              </div>
            )}
            {quota.mustHave.fencesAtMax > 0 && (
              <div className="col-span-2">
                <span className="text-gray-600">Minimum på max højde:</span>
                <span className="ml-2 font-medium">
                  {quota.mustHave.fencesAtMax} spring, {quota.mustHave.oxersAtMax} oxers
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Standard Arena Sizes */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Standard størrelser</h4>
          <div className="space-y-2">
            <div className="text-xs font-medium text-blue-900 mt-3 mb-1">INDENDØRS:</div>
            <button
              onClick={() => onChange({ arenaWidth: 20, arenaLength: 40, isIndoor: true })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              IND-XS: Lille ridehus - 20m × 40m
            </button>
            <button
              onClick={() => onChange({ arenaWidth: 20, arenaLength: 60, isIndoor: true })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              IND-S: Standard ridehus - 20m × 60m
            </button>
            <button
              onClick={() => onChange({ arenaWidth: 25, arenaLength: 55, isIndoor: true })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              IND-M: Medium ridehus - 25m × 55m
            </button>
            <button
              onClick={() => onChange({ arenaWidth: 30, arenaLength: 70, isIndoor: true })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              IND-L: Stor show-arena - 30m × 70m
            </button>
            
            <div className="text-xs font-medium text-blue-900 mt-3 mb-1">UDENDØRS:</div>
            <button
              onClick={() => onChange({ arenaWidth: 40, arenaLength: 60, isIndoor: false })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              OUT-S: Lille udendørs klubbane - 40m × 60m
            </button>
            <button
              onClick={() => onChange({ arenaWidth: 50, arenaLength: 80, isIndoor: false })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              OUT-M: Alm. klub/regions-stævne - 50m × 80m
            </button>
            <button
              onClick={() => onChange({ arenaWidth: 80, arenaLength: 80, isIndoor: false })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              OUT-L: Typisk C/B-stævne - 80m × 80m
            </button>
            <button
              onClick={() => onChange({ arenaWidth: 80, arenaLength: 100, isIndoor: false })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              OUT-XL: Lands- & DM-niveau - 80m × 100m
            </button>
            <button
              onClick={() => onChange({ arenaWidth: 100, arenaLength: 100, isIndoor: false })}
              className="block text-sm text-blue-800 hover:text-blue-900 hover:underline"
            >
              OUT-XXL: International CSI/CHIO - 100m × 100m
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseSettings; 